import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';
import pick from 'lodash/pick';

import { slugify } from 'utils';
import { scrollToAnchor } from 'utils/scroll';
import { confirmRouteLeave } from 'utils/container';

import ViewLoading from 'components/view/loading';

import {
  arrayCapabilitiesToObject,
  objectCapabilitiesToArray,
  objectCapabilitiesUpdate,
} from 'partners/shared/capabilities';
import actions from 'partners/actions';
import Profile from 'partners/components/pages/settings/profile';

const mapStateToProps = (state) => ({
  partner: state.partner,
  suggestedAddresses: state.lookup.suggestedAddresses,
});

const mapDispatchToProps = (dispatch) => ({
  validatePartner: (data) => {
    return dispatch(actions.partner.validate(data));
  },

  fetchPartner(query) {
    return dispatch(actions.partner.fetch(query));
  },

  updatePartner(data) {
    return dispatch(actions.partner.update(data));
  },

  fetchPublishedApps() {
    return dispatch(actions.apps.list({ published: 'true', limit: 100 }));
  },

  flashError: (message) => {
    dispatch(actions.flash.error(message));
  },
});

// onSubmitForm merges the form's values over the whole partner record, so the
// write has to be narrowed back down to what the profile page owns.
const PROFILE_FIELDS = [
  'name',
  'icon',
  'image',
  'tagline',
  'description',
  'timezone',
  'support_email',
  'business',
  'social',
  'industries',
  'tags',
  'capabilities',
  'budget',
  '$currency',
  'currencies',
  'languages',
];

function getProfileValues(settings) {
  return {
    ...settings,
    partner: {
      ...settings.partner,
      capabilities: objectCapabilitiesToArray(
        settings.partner?.capabilities || {},
      ),
    },
  };
}

export class PartnerPage extends React.PureComponent {
  static propTypes = {
    partner: pt.object,
    fetchPartner: pt.func,
    updatePartner: pt.func,
    fetchPublishedApps: pt.func,
    validatePartner: pt.func,
    flashError: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
    notifyError: pt.func.isRequired,
  };

  validatePartnerNameTimer = null;

  constructor(props, context) {
    super(props, context);

    this.state = {
      loaded: false,
      edited: false,
      record: null,
      values: {},
      apps: [],
      errors: {},
      onChangePartnerName: this.onChangePartnerName.bind(this),
      onChangeForm: this.onChangeForm.bind(this),
      onSubmitForm: this.onSubmitForm.bind(this),
      onFormRef: this.onFormRef.bind(this),
      onClickListingAction: this.onClickListingAction.bind(this),
      onClickListingCriteria: this.onClickListingCriteria.bind(this),
    };
  }

  componentDidMount() {
    confirmRouteLeave(this);

    this.load().then(scrollToAnchor);
  }

  async load() {
    const [settings, apps] = await Promise.all([
      this.fetchProfile(),
      this.fetchPublishedApps(),
    ]);

    this.setState({
      loaded: true,
      apps,
      ...(settings && {
        record: settings,
        values: getProfileValues(settings),
      }),
    });
  }

  async fetchProfile() {
    const { fetchPartner } = this.props;
    const partner = await fetchPartner({ $currency: true });

    return partner ? { partner } : null;
  }

  async fetchPublishedApps() {
    const { fetchPublishedApps } = this.props;
    const apps = await fetchPublishedApps();

    return apps?.results || [];
  }

  updateProfile(values, { submitListing = false } = {}) {
    const { updatePartner } = this.props;

    return updatePartner({
      ...pick(values.partner, PROFILE_FIELDS),
      capabilities: objectCapabilitiesUpdate(
        arrayCapabilitiesToObject(values.partner.capabilities),
        this.state.record?.partner?.capabilities || {},
      ),
      ...(submitListing && { listing: { status: 'pending' } }),
    });
  }

  onChangePartnerName(event, name) {
    const { partner, flashError, validatePartner } = this.props;
    const { errors } = this.state;

    clearTimeout(this.validatePartnerNameTimer);

    this.setState({
      errors: {
        ...errors,
        name: undefined,
      },
    });

    if (!name || partner.id === slugify(name)) {
      return;
    }

    this.validatePartnerNameTimer = setTimeout(async () => {
      const result = await validatePartner({
        name,
      });

      if (!result) {
        flashError('Something went wrong!');
      } else if (result.error) {
        this.setState({
          errors: {
            ...errors,
            name: result.error,
          },
        });
      }
    }, 500);
  }

  onChangeForm(values, edited) {
    this.setState((state) => ({
      values: {
        ...state.values,
        ...values,
      },
      edited,
    }));
  }

  onFormRef(form) {
    this.formRef = form;
  }

  async onSubmitForm(values, form = null, { submitListing = false } = {}) {
    const { notifyError } = this.context;

    const mergedValues = {
      ...this.state.values,
      ...values,
      partner: {
        ...this.state.values?.partner,
        ...values?.partner,
      },
    };

    let result;
    try {
      result = await this.updateProfile(mergedValues, { submitListing });
    } catch (err) {
      notifyError(err?.message || 'Something went wrong');
      return false;
    }

    if (!result) {
      notifyError('Something went wrong');
      return false;
    }

    if (result.errors) {
      notifyError(result.errors);
      return false;
    }

    result = await this.fetchProfile();

    this.setState({
      record: result,
      values: getProfileValues(result),
    });

    if (form) {
      form.reset();
    }

    return result;
  }

  onClickListingAction() {
    if (!this.validateProfileForm()) {
      return;
    }

    const status = this.getListingStatus();

    // `pending` and `listed` have no Save changes button, so this is the only
    // save path; `listed` publishes straight away because the listing is live.
    if (status === 'listed') {
      this.submitFromCard(false);
      return;
    }

    this.context.openModal('SubmitListing', {
      status,
      onSubmit: () => this.submitFromCard(status !== 'pending'),
    });
  }

  // The card's button is `type="button"`, so this never reaches
  // `Form.onSubmit`, which is where the native Save changes path validates and
  // then resets. Both have to happen here instead, or the form stays dirty and
  // the card's button stays enabled after a successful save.
  async submitFromCard(submitListing) {
    if (!this.validateProfileForm()) {
      return false;
    }

    const result = await this.onSubmitForm(this.state.values, null, {
      submitListing,
    });

    if (result && this.formRef) {
      this.formRef.reset();
    }

    return result;
  }

  onClickListingCriteria() {
    this.context.openModal('ListingCriteria');
  }

  // The card's button is `type="button"`, so it never goes through
  // `Form.onSubmit` (the only place the native Save changes path validates).
  validateProfileForm() {
    const form = this.formRef;

    if (!form) {
      return true;
    }

    if (!form.validate()) {
      form.focusFirstError();
      return false;
    }

    return true;
  }

  getListingStatus() {
    return this.props.partner?.listing?.status || 'not_listed';
  }

  render() {
    if (!this.state.loaded) {
      return <ViewLoading />;
    }

    return (
      <Profile
        {...this.props}
        {...this.state}
        listingStatus={this.getListingStatus()}
      />
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PartnerPage);
